import { useCallback, useEffect, useMemo, useState } from "react";

import { useIconsQuery } from "../../../../../queries/useIconsQuery";

import { useAlertsSecondaryDomainsDetail } from "./useSecondaryDomainsData";
import { useSecondaryDomainsMutation } from "./useSecondaryDomainsMutation";

type UseSecondaryDomainsStateParams = {
    activationId: string;
    setIsChanged: (isChanged: boolean) => void;
};

export type SecondaryDomain = {
    domain: string;
    icon?: string;
};

export const useSecondaryDomainsState = ({ activationId, setIsChanged }: UseSecondaryDomainsStateParams) => {
    const { data: serverData, isLoading: isDomainsLoading, isError } = useAlertsSecondaryDomainsDetail(activationId);
    const serverDomains = useMemo(() => serverData?.domains ?? [], [serverData]);
    const { data: icons, isLoading: isIconsLoading } = useIconsQuery(
        serverDomains,
        !isDomainsLoading && serverDomains.length > 0,
    );

    const { mutateAsync: saveSecondaryDomains } = useSecondaryDomainsMutation({
        activationId,
        hasExistingData: serverDomains.length > 0,
    });

    const [isDirty, setIsDirty] = useState(false);
    const [secondaryDomains, setSecondaryDomains] = useState<Array<SecondaryDomain>>([]);
    const plainSecondaryDomains = useMemo(() => secondaryDomains.map(({ domain }) => domain), [secondaryDomains]);

    useEffect(() => {
        if (!isDomainsLoading && !isIconsLoading) {
            const newSecondaryDomains = serverDomains.map((domain) => ({
                domain,
                icon: icons[domain],
            }));

            setSecondaryDomains(newSecondaryDomains);
        }
    }, [isDomainsLoading, isIconsLoading, serverDomains, icons]);

    const handleChange = () => {
        setIsDirty(true);
        setIsChanged(true);
    };

    const handleAdd = (item: SecondaryDomain) => {
        setSecondaryDomains((prev) => [item, ...prev]);
        handleChange();
    };

    const handleRemove = (domain: string) => {
        setSecondaryDomains((prev) => prev.filter((existing) => existing.domain !== domain));
        handleChange();
    };

    const handleSave = useCallback(async () => {
        if (!isDirty) {
            return Promise.resolve();
        }

        await saveSecondaryDomains(plainSecondaryDomains);

        setIsDirty(false);
    }, [plainSecondaryDomains, isDirty, saveSecondaryDomains]);

    return {
        plainSecondaryDomains,
        secondaryDomains,
        isError,
        isLoading: isDomainsLoading || isIconsLoading,
        handleSave,
        onAdd: handleAdd,
        onRemove: handleRemove,
    };
};
